/**
 * "How did you hear about us?" — the one-time ask after a first sign-in
 * (2026-09-16).
 *
 * The Create-account form carries the question, but a Google sign-up never
 * sees that form: OAuth lands a session with no metadata to ride along.
 * This card sits under the site banner and above the app, for a signed-in
 * account that is new (PROMPT_WINDOW_DAYS) and has not answered, once per
 * device. Save writes the two profile columns; Not now remembers the skip
 * on this device and says nothing more.
 *
 * It lives OUTSIDE #v2-app like the banner and the ribbon, because views
 * repaint their whole root with innerHTML — anything inside dies on the
 * next render. One probe read per signed-in user id per page load, and
 * only when the device has not already been asked; a failed read (the
 * migration not applied yet) shows nothing at all.
 */
import { showToast } from './toast.js';
import { track } from '../core/analytics.js';
import {
  sourceFieldHtml, wireSourceField, readSourceField,
  wasAsked, markAsked, fetchPromptState, saveSignupSource,
} from '../core/signup-source.js';

function cardHtml() {
  return `
    <form class="v2-source-prompt" data-source-prompt aria-label="How did you hear about us?">
      <p class="v2-source-prompt-lead">Welcome aboard! One quick question:</p>
      ${sourceFieldHtml()}
      <div class="v2-source-prompt-actions">
        <button type="submit" class="v2-btn-email">Save</button>
        <button type="button" class="v2-btn-dialog-close" data-source-skip>Not now</button>
      </div>
    </form>`;
}

export function mount(el, ctx) {
  const { store } = ctx;
  let checkedFor = null; // user id whose probe has run (or is running)
  let showing = false;

  function hide() {
    showing = false;
    el.innerHTML = '';
  }

  function show() {
    if (showing) return;
    showing = true;
    el.innerHTML = cardHtml();
    wireSourceField(el.querySelector('[data-source-prompt]'));
  }

  async function probe(userId) {
    checkedFor = userId;
    if (wasAsked(userId)) return;
    const state = await fetchPromptState(userId);
    // The store may have moved on while the read was in flight
    if (store.get().user?.id !== userId) return;
    if (state.ask) show();
  }

  function render(state) {
    const userId = state.user?.id || null;
    if (!userId) {
      checkedFor = null;
      if (showing) hide();
      return;
    }
    if (checkedFor === userId) return;
    probe(userId);
  }

  async function onSubmit(event) {
    const form = event.target.closest('[data-source-prompt]');
    if (!form) return;
    event.preventDefault();
    const userId = store.get().user?.id;
    const { signupSource, signupSourceDetail } = readSourceField(form);
    if (!signupSource) {
      showToast('Pick an option first — or tap Not now.', { type: 'info' });
      return;
    }
    const submit = form.querySelector('button[type="submit"]');
    submit.disabled = true;
    const { error } = await saveSignupSource(userId, signupSource, signupSourceDetail);
    if (error) {
      // The row is not there to take it (migration pending) or the write
      // was refused — either way, not something to keep asking about.
      markAsked(userId);
      hide();
      showToast('Couldn’t save that right now — thanks anyway.', { type: 'info' });
      return;
    }
    markAsked(userId);
    track('signup_source', { source: signupSource, detail: signupSourceDetail, via: 'prompt' });
    hide();
    showToast('Thanks — that helps us find more explorers.', { type: 'info' });
  }

  function onClick(event) {
    if (!event.target.closest('[data-source-skip]')) return;
    markAsked(store.get().user?.id);
    track('signup_source', { source: null, via: 'prompt-skip' });
    hide();
  }

  el.addEventListener('submit', onSubmit);
  el.addEventListener('click', onClick);
  const unsubscribe = store.subscribe(render);
  render(store.get());

  return () => {
    el.removeEventListener('submit', onSubmit);
    el.removeEventListener('click', onClick);
    unsubscribe();
    hide();
  };
}
